import { AddCircle, Delete, Edit } from "@mui/icons-material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListSubheader,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { useFormik } from "formik";
import {
    MRT_ColumnFiltersState as MRTColumnFiltersState,
    MRT_FullScreenToggleButton as MRTFullScreenToggleButton,
    MRT_PaginationState as MRTPaginationState,
    MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
    MRT_SortingState as MRTSortingState,
    MRT_ToggleDensePaddingButton as MRTToggleDensePaddingButton,
    MRT_ToggleFiltersButton as MRTToggleFiltersButton,
    MRT_ToggleGlobalFilterButton as MRTToggleGlobalFilterButton,
    MRT_ColumnDef,
    MRT_Row,
    MaterialReactTable,
} from "material-react-table";
import { useConfirm } from "material-ui-confirm";
import { compose } from "ramda";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { NumericFormat } from "react-number-format";
import * as yup from "yup";
import { GlobalFilter } from "../../../classes/filter-state.class";
import { Pagination } from "../../../classes/pagination.class";
import { ResponsePagination } from "../../../classes/response-pagination.class";
import Numeric from "../../../components/Common/Input/Numeric/Numeric";
import TextField from "../../../components/Common/Input/TextField/TextField";
import { ManageModal } from "../../../components/Common/ManageModal/ManageModal";
import SkeletonLoading from "../../../components/Common/Skeleton/SkeletonLoading";
import withAppRouter from "../../../components/HoC/withAppRouter";
import { useAuthoize } from "../../../contexts/authorize.context";
import { useAxios } from "../../../contexts/axios.context";
import { Shape } from "../../../utils/ValidateUtils";
import dayjs from "../../../utils/dayjs";
import { downloadFile, numberWithCommas } from "../../../utils/helper";
import { CompanyDto } from "../Company/dto/company.dto";
import { ConfigCategoryDto } from "../ConfigCategory/dto/config-category.dto";
import { EmissionRateDto } from "../EmissionFactor/dto/emission-rate.dto";
import { UnitDto } from "../Unit/dto/unit.dto";
import { ReportTypeOptions, TransactionDownloadDto } from "./dto/transaction-download.dto";
import { TransactionValueDto } from "./dto/transaction-value.dto";
import { TransactionDto } from "./dto/transaction.dto";
import Snackbar from "../../../components/Common/SnackBar/SnackBar";
import { useSnackbar } from "../../../contexts/snackbar.context";
const validationSchema = yup.object<Shape<TransactionDto>>({
    date: yup.date().required("Enter Month and Year"),
    name: yup
        .string()
        .trim()
        .required("Enter Transaction Name")
        .max(100, "Transaction Name cannot be longer than 100 characters."),
    configCategoryId: yup.number().required("Enter Category"),
    values: yup.array().of(
        yup.object<TransactionValueDto>().shape<Record<keyof Pick<TransactionValueDto, "value">, yup.AnySchema>>({
            value: yup.number().required("Enter Transaction Value").not([0], "Transaction Value must more than zero."),
        })
    ),
    unitId: yup.number().required("Enter Category"),
    emissionRateId: yup.number().required("Enter Emission Factor"),
});

const validationSchemaFormDownload = yup.object<Shape<TransactionDownloadDto>>({
    year: yup.date().required("Enter Year"),
});

type SelectValueType = number | "";
const PREFIX_API = `/ghg/transaction-detail`;
const Transaction = <K extends keyof TransactionDto>() => {
    const { userProfile } = useAuthoize();
    const { get, post } = useAxios();
    const confirm = useConfirm();
    const [datasource, setDatasource] = useState<Array<TransactionDto>>([]);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [columnFilters, setColumnFilters] = useState<MRTColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState<GlobalFilter<TransactionDto>>({ filters: [], search: "" });
    const [sorting, setSorting] = useState<MRTSortingState>([{ id: "date", desc: true }]);
    const [editData, setEditData] = useState<TransactionDto>();
    const [pagination, setPagination] = useState<MRTPaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });
    const { enqueueSnackbar, destroySnackbar, enqueueSnackbars } = useSnackbar();
    // const [stackSnack, setStackSnack] = useState(0);

    const [companyId, setCompanyId] = useState<SelectValueType>("");
    const [companyOptions, setCompanyOptions] = useState<Array<CompanyDto>>([]);
    const [companyOptionsIsLoading, setCompanyOptionsIsLoading] = useState<boolean>(false);
    const getCompanyOptionsCallback = useCallback(async () => {
        setIsLoading(true);
        setCompanyOptionsIsLoading(true);
        const response = await get<Array<CompanyDto>>("/ghg/master-company/get/options");
        setCompanyOptions(response.data);
        if (response.data.length > 0) {
            const defaultCompany = response.data[0];
            setCompanyId(response.data[0].id!);
            setColumnFilters([{ id: "companyId", value: defaultCompany.id }]);
        }
        setCompanyOptionsIsLoading(false);
    }, [get, setCompanyOptionsIsLoading, setCompanyOptions, setCompanyId]);

    useMemo(async () => {
        await getCompanyOptionsCallback();
    }, [getCompanyOptionsCallback]);

    const [categoryOptions, setCategoryOptions] = useState<Array<ConfigCategoryDto>>([]);
    const [categoryOptionsIsLoading, setCategoryOptionsIsLoading] = useState<boolean>(false);
    const getCategoryOptionsCallback = useCallback(async () => {
        setCategoryOptionsIsLoading(true);
        const response = await get<Array<ConfigCategoryDto>>(
            `/ghg/master-category/config/get/options?companyId=${companyId}`
        );
        setCategoryOptions(response.data);
        setCategoryOptionsIsLoading(false);
    }, [get, setCategoryOptionsIsLoading, setCategoryOptions, companyId]);

    const [unitOptions, setUnitOptions] = useState<Array<UnitDto>>([]);
    const [unitOptionsIsLoading, setUnitOptionsIsLoading] = useState<boolean>(false);
    const getUnitOptionsCallback = useCallback(async () => {
        setUnitOptionsIsLoading(true);
        const response = await get<Array<UnitDto>>(`/ghg/master-unit/get/options`);
        setUnitOptions(response.data);
        setUnitOptionsIsLoading(false);
    }, [get, setUnitOptionsIsLoading, setUnitOptions]);

    const [emissionFactorOptions, setEmissionFactorOptions] = useState<Array<EmissionRateDto>>([]);
    const [emissionFactorOptionsIsLoading, setEmissionFactorOptionsIsLoading] = useState<boolean>(false);
    const getEmissionFactorOptionsCallback = useCallback(async () => {
        setEmissionFactorOptionsIsLoading(true);
        const response = await get<Array<EmissionRateDto>>(
            `/ghg/master-emission-rate/get/options?companyId=${companyId}`
        );
        setEmissionFactorOptions(response.data);
        setEmissionFactorOptionsIsLoading(false);
    }, [get, setEmissionFactorOptionsIsLoading, setEmissionFactorOptions, companyId]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { data } = await post<ResponsePagination<TransactionDto>>(`${PREFIX_API}/findByCriteria`, {
                criteria: { columnFilters, globalFilter, sorting },
                pagination: { currentPage: pagination.pageIndex + 1, limit: pagination.pageSize } as Pagination,
            });
            setDatasource(data.datasource);
            setRowCount(data.totalRecord);
        } catch (error) {
            setIsError(true);
            return;
        }
        setIsError(false);
        setIsLoading(false);
    }, [post, columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting]);
    useEffect(() => {
        if (companyId) {
            fetchData();
        }
    }, [fetchData, companyId]);

    const handleDeleteRow = useCallback(
        (row: MRT_Row<TransactionDto>) => {
            const id: number = row.original.id!;
            const name: string = row.getValue("name") as string;
            confirm({
                description: (
                    <Typography component="span">
                        Are you sure you want to delete{" "}
                        <Typography component="span" color="lightsalmon" fontWeight="600">
                            {name}
                        </Typography>
                    </Typography>
                ),
            })
                .then(async () => {
                    setIsLoading(true);
                    await post(`${PREFIX_API}/delete/${id}`);
                    fetchData();
                })
                .catch(() => setIsLoading(false));
        },
        [confirm, post, fetchData]
    );

    const columns = useMemo<MRT_ColumnDef<TransactionDto>[]>(
        () => [
            {
                accessorKey: "date" as K,
                header: "Date",
                size: 50,
                enableEditing: false,
                enableHiding: false,
                Cell: ({ cell }) => dayjs(cell.getValue<string>()).format("MMMM YYYY"),
                Filter: ({ column }) => (
                    <MobileDatePicker
                        key="date"
                        closeOnSelect
                        views={["month", "year"]}
                        value={column.getFilterValue() || null}
                        slotProps={{
                            textField: {
                                sx: { minWidth: "120px" },
                                variant: "standard",
                                name: "date",
                            },
                        }}
                        onAccept={(newValue) => {
                            const date = (newValue as Dayjs).set("date", 1).utc(true);
                            column.setFilterValue(date);
                        }}
                    />
                ),
                sortDescFirst: true,
            },
            {
                accessorKey: "configCategory" as K,
                header: "Category",
                Cell: ({ cell }) => (cell.getValue() as ConfigCategoryDto)?.categoryName,
            },
            {
                accessorKey: "name" as K,
                header: "Transaction",
            },
            {
                accessorKey: "value" as K,
                header: "Value",
                filterFn: "between",
                filterVariant: "range",
                size: 140,
                Cell: ({ cell }) => (
                    <Tooltip title={numberWithCommas(cell.getValue<number>())} placement="top-end">
                        <Typography>{numberWithCommas(cell.getValue<number>(), 2, 2)}</Typography>
                    </Tooltip>
                ),
                muiTableBodyCellProps: {
                    align: "right",
                },
            },
            {
                accessorKey: "unit" as K,
                header: "Unit",
                size: 50,
                Cell: ({ cell }) => (cell.getValue() as UnitDto)?.name,
            },
            {
                accessorKey: "emissionRate" as K,
                header: "Emission Factor",
                enableEditing: false,
                enableColumnFilter: false,
                size: 80,
                Cell: ({ cell }) =>
                    (cell.getValue() as EmissionRateDto) && (
                        <Stack direction="column" justifyContent="center" alignItems="center">
                            <Typography variant="caption">{(cell.getValue() as EmissionRateDto)?.name}</Typography>
                            <Typography variant="caption">
                                Rate ({(cell.getValue() as EmissionRateDto)?.value})
                            </Typography>
                        </Stack>
                    ),
            },
            {
                accessorKey: "createdAt",
                header: "Created By",
                enableEditing: false,
                enableColumnFilter: false,
                size: 80,
                Cell: ({ cell }) => (
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Typography variant="caption">{cell.row.original.createdBy}</Typography>
                        <Typography variant="caption">
                            {dayjs(cell.row.original.createdAt).format("DD/MM/YYYY")}
                        </Typography>
                    </Stack>
                ),
                muiTableHeadCellProps: {
                    align: "center",
                },
            },
            {
                accessorKey: "updatedAt",
                header: "Updated By",
                enableEditing: false,
                enableColumnFilter: false,
                size: 80,
                Cell: ({ cell }) => (
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Typography variant="caption">{cell.row.original.updatedBy}</Typography>
                        <Typography variant="caption">
                            {dayjs(cell.row.original.updatedAt).format("DD/MM/YYYY")}
                        </Typography>
                    </Stack>
                ),
                muiTableHeadCellProps: {
                    align: "center",
                },
            },
        ],
        []
    );

    const handleChangeCompany = useCallback((event: SelectChangeEvent<SelectValueType>) => {
        event.preventDefault();
        const targetCompanyId: number = event.target.value as number;
        setCompanyId(targetCompanyId);
        setColumnFilters([{ id: "companyId", value: targetCompanyId }]);
    }, []);
    const [manageModalOpen, setManageModalOpen] = useState(false);

    const requestAllOptions = useCallback(() => {
        getCategoryOptionsCallback();
        getUnitOptionsCallback();
        getEmissionFactorOptionsCallback();
    }, [getCategoryOptionsCallback, getUnitOptionsCallback, getEmissionFactorOptionsCallback]);

    const handleOnCreate = useCallback(() => {
        setManageModalOpen(true);
        setEditData(undefined);
        requestAllOptions();
    }, [setManageModalOpen, setEditData, requestAllOptions]);

    const handleOnEdit = useCallback(
        (row: MRT_Row<TransactionDto>) => {
            setManageModalOpen(true);
            requestAllOptions();
            setEditData({ ...row.original });
        },
        [setManageModalOpen, requestAllOptions]
    );
    const onFormSubmit = async (formValues: TransactionDto) => {
        const emissionRateId = formValues.emissionRateId;
        const emissionRateHistoryId = emissionFactorOptions.find((option) => option.id === emissionRateId)?.historyId;
        if (editData?.id) {
            await post(`${PREFIX_API}/update/${editData?.id}`, { ...formValues, emissionRateHistoryId, companyId });
        } else {
            await post(`${PREFIX_API}`, { ...formValues, emissionRateHistoryId, companyId });
        }
        setManageModalOpen(false);
        formik.setSubmitting(false);
        formik.resetForm();
        fetchData();
    };

    const formik = useFormik<TransactionDto>({
        initialValues: {
            name: editData?.name || "",
            date: dayjs(),
            configCategoryId: "",
            value: undefined,
            values: [new TransactionValueDto(undefined, dayjs())],
            unitId: "",
            emissionRateId: "",
            emissionRateHistoryId: "",
            isActive: editData?.isActive ?? true,
        },
        onSubmit: onFormSubmit,
        validationSchema,
    });
    const { setValues, setFieldValue } = formik;
    useEffect(() => {
        if (editData) {
            const values = editData?.values?.filter((data) => data.id && data.value);
            setValues({
                name: editData?.name || "",
                date: dayjs(editData?.date) || dayjs(),
                configCategoryId: editData?.configCategoryId || "",
                values: values?.length ? values : [new TransactionValueDto(undefined, dayjs())],
                unitId: editData?.unitId || "",
                emissionRateId: editData?.emissionRateId || "",
                isActive: editData?.isActive ?? true,
            });
        }
    }, [setValues, editData]);

    const [downloadFormModalOpen, setDownloadFormModalOpen] = useState(false);

    const onDownloadFormSubmit = async (values: TransactionDownloadDto) => {
        // setStackSnack((prev) => prev + 1);
        const year = (values.year as Dayjs).get("year");
        const reportTypeLabel = ReportTypeOptions.find((option) => option.value === values.reportType)?.label;
        formikDownload.setSubmitting(false);
        formikDownload.resetForm();
        setDownloadFormModalOpen(false);
        const snackBarKey = enqueueSnackbar(
            <Typography>
                {reportTypeLabel} {year}
            </Typography>
        );
        const response = await get("ghg/report/excel/generate", {
            params: { year, companyId, reportType: values.reportType } as TransactionDownloadDto,
            responseType: "arraybuffer",
        });
        destroySnackbar(snackBarKey, true);
        const filename = response.headers["content-disposition"].split("filename=")[1].split(";")[0];
        downloadFile(response.data, `${reportTypeLabel}_${filename}`);
    };

    const formikDownload = useFormik<TransactionDownloadDto>({
        initialValues: {
            year: dayjs(),
            reportType: "SUMMARY",
        },
        onSubmit: onDownloadFormSubmit,
        validationSchema: validationSchemaFormDownload,
    });
    return (
        <Fragment>
            <Box>
                <MaterialReactTable
                    muiTableContainerProps={{ sx: { maxHeight: `calc(100vh - 120px - 96px)` } }}
                    displayColumnDefOptions={{
                        "mrt-row-actions": {
                            muiTableHeadCellProps: {
                                align: "center",
                            },
                            size: 100,
                        },
                    }}
                    getRowId={(row) => row.id?.toString()!}
                    rowCount={rowCount}
                    initialState={{
                        showColumnFilters: true,
                        sorting: [{ id: "date", desc: true }],
                        columnVisibility: { createdAt: false, updatedAt: false },
                    }} //, density: "compact" }}
                    columns={columns}
                    data={datasource}
                    editingMode="modal" //default
                    enableRowNumbers
                    rowNumberMode="static"
                    enableEditing
                    enableMultiSort
                    manualFiltering
                    manualPagination
                    manualSorting
                    muiToolbarAlertBannerProps={
                        isError
                            ? {
                                  color: "error",
                                  children: "Error loading data",
                              }
                            : undefined
                    }
                    state={{
                        columnFilters,
                        globalFilter: globalFilter.search,
                        isLoading: isLoading,
                        pagination,
                        showAlertBanner: isError,
                        sorting,
                    }}
                    onColumnFiltersChange={setColumnFilters}
                    onGlobalFilterChange={(search) => setGlobalFilter((prev) => ({ ...prev, search }))}
                    onPaginationChange={setPagination}
                    onSortingChange={setSorting}
                    renderRowActions={({ row, table }) => (
                        <SkeletonLoading loading={isLoading}>
                            <Box sx={{ display: "flex", gap: "1rem" }}>
                                <Tooltip arrow placement="left" title="Edit">
                                    <IconButton onClick={() => handleOnEdit(row)}>
                                        <Edit />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip arrow placement="right" title="Delete">
                                    <IconButton color="error" onClick={() => handleDeleteRow(row)}>
                                        <Delete />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </SkeletonLoading>
                    )}
                    renderTopToolbarCustomActions={() => (
                        <Box sx={{ display: "flex", gap: "1rem", p: "4px" }}>
                            <Button
                                color="secondary"
                                onClick={handleOnCreate}
                                variant="contained"
                                startIcon={<AddCircle />}
                                size="small"
                            >
                                Create New
                            </Button>
                            <FormControl sx={{ width: "200px" }}>
                                <SkeletonLoading loading={companyOptionsIsLoading}>
                                    <InputLabel id="select-company" size="small">
                                        Company
                                    </InputLabel>
                                    <Select
                                        labelId="select-company"
                                        id="select-company"
                                        defaultValue={companyId}
                                        value={companyId}
                                        label="Company"
                                        size="small"
                                        onChange={handleChangeCompany}
                                        disabled={companyOptionsIsLoading}
                                    >
                                        {companyOptions.map((option) => (
                                            <MenuItem value={option.id} key={option.id}>
                                                {`${option.companyCode} - ${option.companyName}`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </SkeletonLoading>
                            </FormControl>
                        </Box>
                    )}
                    renderToolbarInternalActions={({ table }) => (
                        <Box>
                            <MRTToggleGlobalFilterButton table={table} />
                            <IconButton onClick={() => setDownloadFormModalOpen(true)}>
                                <CloudDownloadIcon />
                            </IconButton>
                            <MRTToggleFiltersButton table={table} />
                            <MRTShowHideColumnsButton table={table} />
                            <MRTToggleDensePaddingButton table={table} />
                            <MRTFullScreenToggleButton table={table} />
                        </Box>
                    )}
                />
                <ManageModal<TransactionDto>
                    open={manageModalOpen}
                    onClose={() => setManageModalOpen(false)}
                    title={!editData ? "Create New Transaction" : "Edit Transaction"}
                    formik={formik}
                    formComponent={
                        <Fragment>
                            <MobileDatePicker
                                key="date"
                                label={"Month and Year"}
                                views={["month", "year"]}
                                onChange={(value) => setFieldValue("date", value, true)}
                                value={formik.values.date}
                                slotProps={{
                                    textField: {
                                        variant: "standard",
                                        name: "date",
                                        value: formik.values.date,
                                        error: formik.touched.date && Boolean(formik.errors.date),
                                        helperText: formik.touched.date && formik.errors.date,
                                    },
                                }}
                            />
                            <FormControl
                                variant="standard"
                                error={!!(formik.touched.configCategoryId && formik.errors.configCategoryId)}
                            >
                                <SkeletonLoading loading={categoryOptionsIsLoading}>
                                    <InputLabel htmlFor="configCategoryId" id="configCategoryId" size="small">
                                        Category
                                    </InputLabel>
                                    <Select
                                        aria-describedby="config-category-id-helper-text"
                                        variant="standard"
                                        labelId="configCategoryId"
                                        id="configCategoryId"
                                        name="configCategoryId"
                                        defaultValue={formik.values.configCategoryId}
                                        value={formik.values.configCategoryId}
                                        label="Category"
                                        onChange={formik.handleChange}
                                        disabled={categoryOptionsIsLoading}
                                        error={
                                            formik.touched.configCategoryId && Boolean(formik.errors.configCategoryId)
                                        }
                                    >
                                        {categoryOptions.map((option) => (
                                            <MenuItem value={option.id} key={option.id}>
                                                {`${option.categoryName}`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.configCategoryId && formik.errors.configCategoryId ? (
                                        <FormHelperText id="category-id-helper-text">Enter Category</FormHelperText>
                                    ) : undefined}
                                </SkeletonLoading>
                            </FormControl>
                            <TextField
                                variant="standard"
                                value={formik.values.name}
                                key="name"
                                name="name"
                                label="Name"
                                onChange={formik.handleChange}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                            />
                            <List
                                sx={{ width: "100%", m: 0, p: 0 }}
                                subheader={
                                    <ListSubheader
                                        sx={{
                                            px: 0,
                                            backgroundColor: "transparent",
                                            lineHeight: 0,
                                            justifyContent: "space-between",
                                            display: "flex",
                                            flexDiection: "row",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Box>
                                            Values (<SumValues values={formik.values.values} />)
                                        </Box>
                                        <IconButton
                                            onClick={() => {
                                                const values = formik.values.values;
                                                values?.push(new TransactionValueDto(undefined, dayjs()));
                                                setFieldValue("values", values);
                                            }}
                                        >
                                            <AddCircleOutlineIcon color="primary" />
                                        </IconButton>
                                    </ListSubheader>
                                }
                            >
                                {formik.values.values?.map((data, index) => {
                                    const fieldKey = `values[${index}]`;
                                    return (
                                        <ListItem key={index} sx={{ m: 0, px: 0, width: "100%" }}>
                                            <Grid2 container sx={{ width: "100%", justifyContent: "space-between" }}>
                                                <Grid2 xs={10}>
                                                    {
                                                        <Typography variant="caption" color="GrayText">
                                                            Creator: {data.createdBy || userProfile?.email} (
                                                            {dayjs(data.createdAt).format("DD/MM/YYYY")})
                                                        </Typography>
                                                    }
                                                    <TextField
                                                        fullWidth
                                                        variant="standard"
                                                        value={data.value}
                                                        name={fieldKey}
                                                        // label={}
                                                        onChange={({ target: { value } }) => {
                                                            const numberValue = Number(value);
                                                            setFieldValue(fieldKey, {
                                                                ...data,
                                                                value: numberValue,
                                                            } as TransactionValueDto);
                                                        }}
                                                        InputProps={{
                                                            inputComponent: Numeric as any,
                                                        }}
                                                        error={
                                                            formik.touched.values &&
                                                            Boolean(
                                                                (formik.errors.values?.[index] as TransactionValueDto)
                                                                    ?.value
                                                            )
                                                        }
                                                        helperText={
                                                            formik.touched.values &&
                                                            (formik.errors.values?.[index] as TransactionValueDto)
                                                                ?.value
                                                        }
                                                    />
                                                    {data.updatedBy && (
                                                        <Box>
                                                            <Typography variant="caption" color="GrayText">
                                                                Updater: {data.updatedBy} (
                                                                {dayjs(data.updatedAt).format("DD/MM/YYYY")})
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Grid2>
                                                <Grid2
                                                    xs="auto"
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <IconButton
                                                        color="error"
                                                        disabled={formik.values.values?.length === 1}
                                                        onClick={() => {
                                                            const values = formik.values.values;
                                                            setFieldValue(
                                                                "values",
                                                                values?.filter((_, i) => i !== index)
                                                            );
                                                        }}
                                                    >
                                                        <RemoveCircleOutlineIcon />
                                                    </IconButton>
                                                </Grid2>
                                            </Grid2>
                                        </ListItem>
                                    );
                                })}
                            </List>
                            <FormControl variant="standard" error={!!(formik.touched.unitId && formik.errors.unitId)}>
                                <SkeletonLoading loading={unitOptionsIsLoading}>
                                    <InputLabel htmlFor="unitId" id="unitId" size="small">
                                        Unit
                                    </InputLabel>
                                    <Select
                                        aria-describedby="unit-id-helper-text"
                                        variant="standard"
                                        labelId="unitId"
                                        id="unitId"
                                        name="unitId"
                                        defaultValue={formik.values.unitId}
                                        value={formik.values.unitId}
                                        label="Unit"
                                        onChange={formik.handleChange}
                                        disabled={unitOptionsIsLoading}
                                        error={formik.touched.unitId && Boolean(formik.errors.unitId)}
                                    >
                                        {unitOptions.map((option) => (
                                            <MenuItem value={option.id} key={option.id}>
                                                {`${option.name}`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.unitId && formik.errors.unitId ? (
                                        <FormHelperText id="unit-id-helper-text">Enter Unit</FormHelperText>
                                    ) : undefined}
                                </SkeletonLoading>
                            </FormControl>
                            <FormControl
                                variant="standard"
                                error={!!(formik.touched.emissionRateId && formik.errors.emissionRateId)}
                            >
                                <SkeletonLoading loading={emissionFactorOptionsIsLoading}>
                                    <InputLabel htmlFor="emissionRateId" id="emissionRateId" size="small">
                                        Emission Factor
                                    </InputLabel>
                                    <Select
                                        aria-describedby="emission-rate-id-helper-text"
                                        variant="standard"
                                        labelId="emissionRateId"
                                        id="emissionRateId"
                                        name="emissionRateId"
                                        defaultValue={formik.values.emissionRateId}
                                        value={formik.values.emissionRateId}
                                        label="Emission Factor"
                                        onChange={formik.handleChange}
                                        disabled={emissionFactorOptionsIsLoading}
                                        error={formik.touched.emissionRateId && Boolean(formik.errors.emissionRateId)}
                                    >
                                        {emissionFactorOptions.map((option) => (
                                            <MenuItem value={option.id} key={option.id}>
                                                {`${option.name} : Rate (${option.value})`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {editData?.emissionRate?.value && (
                                        <Box>
                                            <Typography variant="caption" color="orange">
                                                Present factor rate is <b>{editData?.emissionRate?.value}</b>
                                            </Typography>
                                        </Box>
                                    )}
                                    {formik.touched.emissionRateId && formik.errors.emissionRateId ? (
                                        <FormHelperText id="emission-rate-id-helper-text">
                                            Enter Emission Factor
                                        </FormHelperText>
                                    ) : undefined}
                                </SkeletonLoading>
                            </FormControl>
                        </Fragment>
                    }
                />
                <ManageModal<TransactionDto>
                    open={downloadFormModalOpen}
                    onClose={() => setDownloadFormModalOpen(false)}
                    title={"Download Transactions"}
                    formik={formikDownload}
                    submitTitle="Export"
                    disabled={enqueueSnackbars.length >= 3}
                    formComponent={
                        <Fragment>
                            <FormControl variant="standard" error={!!(formik.touched.unitId && formik.errors.unitId)}>
                                <InputLabel htmlFor="reportType" id="reportType" size="small">
                                    Report Type
                                </InputLabel>
                                <Select
                                    aria-describedby="unit-id-helper-text"
                                    variant="standard"
                                    labelId="reportType"
                                    id="typreportTypee"
                                    name="reportType"
                                    defaultValue={formikDownload.values.reportType}
                                    value={formikDownload.values.reportType}
                                    label="Unit"
                                    onChange={formikDownload.handleChange}
                                    error={
                                        formikDownload.touched.reportType && Boolean(formikDownload.errors.reportType)
                                    }
                                >
                                    {ReportTypeOptions.map((option) => (
                                        <MenuItem value={option.value} key={option.value}>
                                            {`${option.label}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <MobileDatePicker
                                key="year"
                                label={"Year"}
                                views={["year"]}
                                onChange={(value) => formikDownload.setFieldValue("year", value, true)}
                                value={formikDownload.values.year}
                                slotProps={{
                                    textField: {
                                        variant: "standard",
                                        name: "year",
                                        value: formikDownload.values.year,
                                        error: formikDownload.touched.year && Boolean(formikDownload.errors.year),
                                        helperText: formikDownload.touched.year && formikDownload.errors.year,
                                    },
                                }}
                            />
                        </Fragment>
                    }
                />
                <Snackbar withLoading anchorOrigin={{ horizontal: "left", vertical: "bottom" }} />
            </Box>
        </Fragment>
    );
};

type SumValuesProps = {
    values?: Array<TransactionValueDto>;
};
const SumValues = ({ values }: SumValuesProps) => {
    const sumMemo = useMemo(() => values?.reduce((sum, data) => sum + Number(data.value), 0) || 0, [values]);
    return (
        <NumericFormat
            value={sumMemo}
            thousandSeparator
            valueIsNumericString
            displayType="text"
            decimalScale={2}
            fixedDecimalScale
        />
    );
};

const TransactionWrapped = compose(withAppRouter);
export default TransactionWrapped(Transaction);
